// CompetitionScheduleList — embeddable, self-contained schedule section
// (UI-SPEC §8 desktop, §20/K9 item 2 mobile, §13 suffix chips, §22 formats).
//
// Props only — NO API calls, no page context. The caller normalises the data
// (team_reverse is already resolved BE-side; home is always left).
//
// Rows are deliberately NOT links and carry no navigation: the match-detail
// destination does not exist yet ("their destination follows later",
// UI-SPEC §1.7) — styled per handoff, but no dead link and no hover
// affordance that promises a click.
//
// "Empty renders nothing": no rounds AND no matches -> null; a match field
// without data is simply absent (no '-' / 'n/a' placeholders anywhere).
import React from "react";
import CompetitionRoundNavigator from "./shared/CompetitionRoundNavigator";
import { CompetitionCrest } from "./shared/CompetitionMedia";
import {
  C,
  FONT_FAMILY,
  formatKickoffTime,
  formatMatchDate,
  scoreText,
} from "./shared/competitionsUi";
import useCompetitionsTr from "./shared/useCompetitionsTr";
import useContainerWidth from "./shared/useContainerWidth";

// §13 chip anatomy verbatim: 11px mono, #64748B, 1px #E2E8F0, 5px 8px, r2.
const SuffixChip = ({ children }) => (
  <span
    style={{
      fontFamily: "ui-monospace,Menlo,monospace",
      fontSize: 11,
      color: C.ink500,
      border: `1px solid ${C.border}`,
      padding: "2px 6px",
      borderRadius: 2,
      whiteSpace: "nowrap",
    }}
  >
    {children}
  </span>
);

// Result suffix (§13): penalties are a suffix, NEVER a replacement score;
// pens implies extra time, so "on pens" wins over "a.E.T.".
const suffixOf = (match, tr) => {
  if (match.penalties && match.penalties.home != null) return tr("on pens");
  if (match.hasOvertime) return tr("a.E.T.");
  return null;
};

const isFinished = (match) =>
  match.homeScore != null && match.awayScore != null;

// Center cell content: final score for played matches, kickoff time (or TBD)
// for future ones (§8).
const CenterScore = ({ match, tr, compact = false }) => {
  const finished = isFinished(match);
  const suffix = suffixOf(match, tr);
  const agg =
    match.agg && match.agg.home != null && match.agg.away != null
      ? `${tr("agg.")} ${scoreText(match.agg.home, match.agg.away)}`
      : null;
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: compact ? "flex-end" : "center",
        gap: 3,
      }}
    >
      <span
        style={{
          fontSize: compact ? 14.5 : 15,
          fontWeight: 700,
          color: C.ink900,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
        }}
      >
        {finished
          ? scoreText(match.homeScore, match.awayScore)
          : match.tbd
            ? tr("TBD")
            : formatKickoffTime(match.date)}
      </span>
      {finished && suffix && <SuffixChip>{suffix}</SuffixChip>}
      {finished && agg && (
        <span
          style={{
            fontSize: 11.5,
            color: C.ink500,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
          }}
        >
          {agg}
        </span>
      )}
    </div>
  );
};

const metaDateOf = (match, tr, locale) => {
  const date = formatMatchDate(match.date, locale);
  const time = match.tbd ? tr("TBD") : formatKickoffTime(match.date);
  const leg =
    match.leg === 1 ? tr("1st leg") : match.leg === 2 ? tr("2nd leg") : null;
  // K9 meta line is always "{d} · {t}" (+ leg), the venue is appended by the
  // caller of this helper; the score itself sits on the home line.
  return [date, time, leg].filter(Boolean).join(" · ");
};

const DesktopRow = ({ match, tr, locale }) => {
  const finished = isFinished(match);
  const leg =
    match.leg === 1 ? tr("1st leg") : match.leg === 2 ? tr("2nd leg") : null;
  const dateText = [
    formatMatchDate(match.date, locale),
    finished ? null : match.tbd ? tr("TBD") : formatKickoffTime(match.date),
    leg,
  ]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "150px 1fr 88px 1fr 220px",
        alignItems: "center",
        gap: 16,
        padding: "0 20px",
        borderBottom: `1px solid ${C.rowDivider}`,
        minHeight: 56,
      }}
    >
      <span
        style={{
          fontSize: 13,
          color: C.ink500,
          fontVariantNumeric: "tabular-nums",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {dateText}
      </span>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          gap: 10,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: 14.5,
            fontWeight: 500,
            color: C.ink900,
            textAlign: "right",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {match.home && match.home.name}
        </span>
        <CompetitionCrest
          name={match.home && match.home.name}
          logo={match.home && match.home.logo}
          size={24}
        />
      </span>
      <CenterScore match={match} tr={tr} />
      <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
        <CompetitionCrest
          name={match.away && match.away.name}
          logo={match.away && match.away.logo}
          size={24}
        />
        <span
          style={{
            fontSize: 14.5,
            fontWeight: 500,
            color: C.ink900,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {match.away && match.away.name}
        </span>
      </span>
      <span
        style={{
          fontSize: 12.5,
          color: C.ink400,
          textAlign: "right",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {match.venue || ""}
      </span>
    </div>
  );
};

// K9 mobile card: meta line "{d} · {t} · {venue}", home line with the score
// right-aligned, away line below.
const MobileCard = ({ match, tr, locale }) => {
  const meta = [metaDateOf(match, tr, locale), match.venue]
    .filter(Boolean)
    .join(" · ");
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: 3,
        padding: "10px 14px",
        display: "flex",
        flexDirection: "column",
        gap: 8,
      }}
    >
      {meta && (
        <span
          style={{
            fontSize: 11.5,
            color: C.ink500,
            fontVariantNumeric: "tabular-nums",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {meta}
        </span>
      )}
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CompetitionCrest
          name={match.home && match.home.name}
          logo={match.home && match.home.logo}
          size={22}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            color: C.ink900,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {match.home && match.home.name}
        </span>
        <CenterScore match={match} tr={tr} compact />
      </span>
      <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <CompetitionCrest
          name={match.away && match.away.name}
          logo={match.away && match.away.logo}
          size={22}
        />
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 14,
            fontWeight: 500,
            color: C.ink900,
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
          }}
        >
          {match.away && match.away.name}
        </span>
      </span>
    </div>
  );
};

const CompetitionScheduleList = ({
  rounds = [],
  round,
  onRoundChange,
  matches = [],
  title,
}) => {
  const { tr, currentLanguage } = useCompetitionsTr();
  const [containerRef, width] = useContainerWidth(1024);
  const compact = width < 560;
  const locale = currentLanguage || "en";

  // "Empty renders nothing" — no data at all -> no section, no placeholder.
  if (!rounds.length && !matches.length) return null;

  const roundLabelOf = (r) => {
    if (!r) return "";
    if (r.label) return tr(r.label);
    // Unnumbered bucket (flattenSchedule's UNNUMBERED_ROUND sentinel — cup
    // stages ship round_num:null, BE PR #411): no number to template into
    // "Round {n}", so a neutral title. Checked structurally (non-numeric
    // round) to keep this component free of page imports.
    if (!Number.isFinite(Number(r.round))) return tr("All fixtures");
    const template = tr("Round {round}");
    return template.replace("{round}", String(r.round));
  };

  return (
    <section ref={containerRef} style={{ fontFamily: FONT_FAMILY }}>
      {/* The section title is the PAGE's (section kicker pattern). Passing
          none renders only the round navigator, right-aligned. NB: the flat
          i18n namespace already owns "Schedule" as the drafts-scheduler VERB
          (DE "Terminieren") — a default heading keyed on it would translate
          wrongly, so there deliberately is no default title here; K10's
          "Spielplan" comes from the embedding page's own heading. */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: title ? "space-between" : "flex-end",
          gap: 12,
          marginBottom: 14,
          flexWrap: "wrap",
        }}
      >
        {title && (
          <span
            style={{
              fontSize: compact ? 16 : 19,
              fontWeight: 700,
              color: C.ink900,
              letterSpacing: "-.02em",
            }}
          >
            {title}
          </span>
        )}
        <CompetitionRoundNavigator
          rounds={rounds}
          round={round}
          onRoundChange={onRoundChange}
          labelOf={roundLabelOf}
          compact={compact}
        />
      </div>
      {matches.length > 0 &&
        (compact ? (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {matches.map((match) => (
              <MobileCard key={match.id} match={match} tr={tr} locale={locale} />
            ))}
          </div>
        ) : (
          <div
            style={{
              border: `1px solid ${C.border}`,
              borderRadius: 3,
              overflow: "hidden",
              background: "#fff",
            }}
          >
            {matches.map((match) => (
              <DesktopRow key={match.id} match={match} tr={tr} locale={locale} />
            ))}
          </div>
        ))}
    </section>
  );
};

export default CompetitionScheduleList;
